import React from "react";
import { useParams } from "react-router-dom";
import {
  FusionProvider,
  useCredentials,
  useBridgePort,
  useIframeCredentials,
} from "./hooks";

/**
 * App host component for development mode
 * Loads Streamlit apps from localhost:port during development
 * Wraps content with FusionProvider to receive credentials and bridge port
 */
export const AppHostDevelopment: React.FC = () => {
  return (
    <FusionProvider>
      <AppHostDevelopmentContent />
    </FusionProvider>
  );
};

/**
 * Inner component that consumes Fusion context
 */
const AppHostDevelopmentContent: React.FC = () => {
  const { port } = useParams<{ port: string }>();
  const { credentials } = useCredentials();
  const bridgePort = useBridgePort();

  // Setup iframe communication (must be called before any conditional returns)
  // In development mode, we MUST have a port
  if (!port) {
    throw new Error(
      "AppHostDevelopment requires a port parameter - this should never happen!",
    );
  }
  // The development server at this origin must be configured to use HTTPS
  const iframeOrigin = `https://localhost:${port}`;
  const { iframeRef } = useIframeCredentials(credentials, iframeOrigin, bridgePort);

  // Wait for credentials from Fusion
  if (!credentials || !iframeRef) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "16px",
          color: "#666",
          fontFamily: "sans-serif",
        }}
      >
        Waiting for credentials from Fusion...
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      src={iframeOrigin}
      style={{
        display: "block",
        width: "100%",
        height: "100vh",
        border: "none",
      }}
      title="Streamlit App Development"
      allow="accelerometer; autoplay; camera; clipboard-write; encrypted-media; fullscreen; geolocation; gyroscope; microphone; picture-in-picture"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads allow-fullscreen"
    />
  );
};

export default AppHostDevelopment;
